import { v4 as uuid } from 'uuid';

import {
  CVData,
  CVEntry,
  CVPersonalInfo,
  CVSection,
  CVSectionType,
  CVSettings,
  CV_SECTION_DEFAULTS,
} from '../types';

export const createDefaultPersonalInfo = (): CVPersonalInfo => ({
  fullName: '',
  jobTitle: '',
  email: '',
  phone: '',
  location: '',
  website: '',
  linkedin: '',
  github: '',
  summary: '',
  photoUrl: '',
  gdprConsent: true,
});

export const createDefaultEntry = (overrides?: Partial<CVEntry>): CVEntry => ({
  id: uuid(),
  title: '',
  subtitle: '',
  location: '',
  startDate: '',
  endDate: '',
  description: '',
  url: '',
  urlLabel: '',
  previewUrl: '',
  previewUrlLabel: '',
  tags: [],
  ...overrides,
});

export const createDefaultSection = (
  type: CVSectionType,
  overrides?: Partial<CVSection>
): CVSection => ({
  id: uuid(),
  type,
  title: CV_SECTION_DEFAULTS[type],
  entries: [createDefaultEntry()],
  ...overrides,
});

export const createDefaultCVData = (): CVData => ({
  personalInfo: createDefaultPersonalInfo(),
  sections: [
    createDefaultSection('experience'),
    createDefaultSection('education'),
    createDefaultSection('skills', { entries: [] }),
    createDefaultSection('projects'),
  ],
});

/**
 * Strips protocol (http/https), www., and trailing slashes from URLs
 * for cleaner display on CVs. e.g. "https://www.github.com/bigsondev/" → "github.com/bigsondev"
 */
export const formatContactDisplay = (value: string): string => {
  return value.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
};

export const createDefaultSettings = (): CVSettings => ({
  fontFamily: 'Poppins',
  accentColor: '#6B46C1',
  accentOpacity: 1,
  fontSize: 'medium',
  headerBackground: true,
  sidebarReversed: false,
  showPhoto: false,
});

export const hexToRgba = (hex: string, opacity: number): string => {
  if (opacity >= 1) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const getAutoAvatarUrl = (name: string): string =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6B46C1&color=fff&size=128`;

export const getDisplayPhotoUrl = (photoUrl: string, fullName: string): string =>
  photoUrl || getAutoAvatarUrl(fullName.trim() || 'CV');
